import { useState } from 'react';
import emailjs from '@emailjs/browser';
import { emailConfig } from '../emailConfig';
import './Search.css';

function Search() {
  const [searchParams, setSearchParams] = useState({
    location: '',
    propertyType: 'any',
    minPrice: '',
    maxPrice: '',
    bedrooms: 'any',
    bathrooms: 'any',
    name: '',
    email: '',
    phone: '',
    additionalRequirements: ''
  });

  const [searchSubmitted, setSearchSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSearchParams(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // EmailJS configuration - You'll need to set these up at emailjs.com
      const templateParams = {
        to_email: 'bradkashani@gmail.com',
        from_name: searchParams.name,
        from_email: searchParams.email,
        location: searchParams.location,
        property_type: searchParams.propertyType === 'any' ? 'Any Type' : searchParams.propertyType,
        min_price: searchParams.minPrice ? `$${parseInt(searchParams.minPrice).toLocaleString()}` : 'No Min',
        max_price: searchParams.maxPrice ? `$${parseInt(searchParams.maxPrice).toLocaleString()}` : 'No Max',
        bedrooms: searchParams.bedrooms === 'any' ? 'Any' : `${searchParams.bedrooms}+`,
        bathrooms: searchParams.bathrooms === 'any' ? 'Any' : `${searchParams.bathrooms}+`,
        name: searchParams.name,
        email: searchParams.email,
        phone: searchParams.phone || 'Not provided',
        additional_requirements: searchParams.additionalRequirements || 'None specified',
        message: `New property wish list request:

Client Information:
Name: ${searchParams.name}
Email: ${searchParams.email}
Phone: ${searchParams.phone || 'Not provided'}

Property Requirements:
Location: ${searchParams.location}
Property Type: ${searchParams.propertyType === 'any' ? 'Any Type' : searchParams.propertyType}
Price Range: ${searchParams.minPrice ? `$${parseInt(searchParams.minPrice).toLocaleString()}` : 'No Min'} - ${searchParams.maxPrice ? `$${parseInt(searchParams.maxPrice).toLocaleString()}` : 'No Max'}
Bedrooms: ${searchParams.bedrooms === 'any' ? 'Any' : `${searchParams.bedrooms}+`}
Bathrooms: ${searchParams.bathrooms === 'any' ? 'Any' : `${searchParams.bathrooms}+`}

Additional Requirements:
${searchParams.additionalRequirements || 'None specified'}

Please follow up with this potential client.`,
        subject: 'New Property Wish List Request from Website'
      };

      // Initialize EmailJS
      emailjs.init(emailConfig.publicKey);
      
      await emailjs.send(
        emailConfig.serviceId,
        emailConfig.searchTemplateId,
        templateParams
      );

      setSearchSubmitted(true);
      setSubmitStatus('success');
      
      // Reset form after successful submission
      setSearchParams({
        location: '',
        propertyType: 'any',
        minPrice: '',
        maxPrice: '',
        bedrooms: 'any',
        bathrooms: 'any',
        name: '',
        email: '',
        phone: '',
        additionalRequirements: ''
      });
    } catch (error) {
      console.error('Detailed error sending email:', error);
      console.error('Error message:', error.message);
      console.error('Error status:', error.status);
      console.error('Error text:', error.text);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="search">
      <div className="container">
        <h1>Property Wish List</h1>
        <p className="search-subtitle">Fill the form below based on your requirements for the desired property</p>
        
        <div className="search-content">
          <div className="search-form-section">
            <form onSubmit={handleSearch} className="search-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="location">Locations</label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={searchParams.location}
                    onChange={handleInputChange}
                    placeholder="List up to 3 cities"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="propertyType">Property Type</label>
                  <select
                    id="propertyType"
                    name="propertyType"
                    value={searchParams.propertyType}
                    onChange={handleInputChange}
                  >
                    <option value="any">Any Type</option>
                    <option value="single-family">Single Family Home</option>
                    <option value="condo">Condo/Townhome</option>
                    <option value="multi-family">Multi-Family</option>
                    <option value="land">Land/Lot</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="minPrice">Min Price</label>
                  <select
                    id="minPrice"
                    name="minPrice"
                    value={searchParams.minPrice}
                    onChange={handleInputChange}
                  >
                    <option value="">No Min</option>
                    <option value="500000">$500,000</option>
                    <option value="750000">$750,000</option>
                    <option value="1000000">$1,000,000</option>
                    <option value="1500000">$1,500,000</option>
                    <option value="2000000">$2,000,000</option>
                    <option value="3000000">$3,000,000</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="maxPrice">Max Price</label>
                  <select
                    id="maxPrice"
                    name="maxPrice"
                    value={searchParams.maxPrice}
                    onChange={handleInputChange}
                  >
                    <option value="">No Max</option>
                    <option value="750000">$750,000</option>
                    <option value="1000000">$1,000,000</option>
                    <option value="1500000">$1,500,000</option>
                    <option value="2000000">$2,000,000</option>
                    <option value="3000000">$3,000,000</option>
                    <option value="4000000">$4,000,000</option>
                    <option value="5000000">$5,000,000+</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="bedrooms">Bedrooms</label>
                  <select
                    id="bedrooms"
                    name="bedrooms"
                    value={searchParams.bedrooms}
                    onChange={handleInputChange}
                  >
                    <option value="any">Any</option>
                    <option value="1">1+</option>
                    <option value="2">2+</option>
                    <option value="3">3+</option>
                    <option value="4">4+</option>
                    <option value="5">5+</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="bathrooms">Bathrooms</label>
                  <select
                    id="bathrooms"
                    name="bathrooms"
                    value={searchParams.bathrooms}
                    onChange={handleInputChange}
                  >
                    <option value="any">Any</option>
                    <option value="1">1+</option>
                    <option value="2">2+</option>
                    <option value="3">3+</option>
                    <option value="4">4+</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={searchParams.name}
                    onChange={handleInputChange}
                    placeholder="Your full name"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={searchParams.email}
                    onChange={handleInputChange}
                    placeholder="your.email@example.com"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">Phone</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={searchParams.phone}
                    onChange={handleInputChange}
                    placeholder="(650) 123-4567"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group full-width">
                  <label htmlFor="additionalRequirements">Additional Requirements</label>
                  <textarea
                    id="additionalRequirements"
                    name="additionalRequirements"
                    value={searchParams.additionalRequirements}
                    onChange={handleInputChange}
                    placeholder="Any specific requirements, preferences, or additional details..."
                    rows="4"
                  />
                </div>
              </div>

              <button type="submit" className="search-btn" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Submit Form'}
              </button>
            </form>

            {submitStatus === 'success' && (
              <div className="search-result success">
                <h3>Search Submitted Successfully!</h3>
                <p>Your property search request has been sent to Gita. She'll personally search for properties matching your criteria and get back to you with available options.</p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="search-result error">
                <h3>Submission Error</h3>
                <p>There was an issue sending your search request. Please contact Gita directly:</p>
                <div className="contact-info">
                  <span className="phone-desktop">(650) 283-3537</span>
                  <a href="tel:650-283-3537" className="phone-mobile">Call (650) 283-3537</a>
                  <a href="mailto:bradkashani@gmail.com" className="contact-link">bradkashani@gmail.com</a>
                </div>
              </div>
            )}
          </div>

          <div className="search-info">
            <h3>Why Work With Me?</h3>
            
            <div className="info-section">
              <h4>🏠 Local Market Expert</h4>
              <p>30 years of Real Estate and Mortgage experience in Silicon Valley</p>
            </div>

            <div className="info-section">
              <h4>🔍 Comprehensive Search</h4>
              <p>Access to MLS listings, off-market properties, and coming-soon inventory.</p>
            </div>

            <div className="info-section">
              <h4>💰 Financial Guidance</h4>
              <p>Connections to 120+ lenders for optimal financing options.</p>
            </div>

            <div className="info-section">
              <h4>🤝 Personalized Service</h4>
              <p>Custom property tours, market analysis, and negotiation support.</p>
            </div>

            <div className="cta-section">
              <h4>Ready to Start Your Search?</h4>
              <p>Let's discuss your needs and find your perfect property.</p>
              <span className="phone-desktop">(650) 283-3537</span>
              <a href="tel:650-283-3537" className="phone-mobile cta-btn">Call (650) 283-3537</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Search;